import { Component, OnInit, OnDestroy, Inject, Output, EventEmitter, Input, SimpleChanges, OnChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { L10nLocale, L10N_LOCALE } from 'angular-l10n';
import {
  catchError,
  debounceTime,
  finalize,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  pairwise,
  startWith,
  Subject,
  Subscription,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { GroupsApi, GroupTreeNode, PagedGroups, PagedGroupTreeNodeResponse } from 'src/app/hickory-api';
import { AuthService } from 'src/app/shared/authentication/auth.service';

interface GroupNode {
  group: GroupTreeNode;
  indentLevel: number;
  expanded?: boolean;
  loading?: boolean;
}

type OptionalClassification = 'All' | 'Public' | 'Internal';

@Component({
  selector: 'app-user-groups',
  templateUrl: './user-groups.component.html',
  styleUrls: ['./user-groups.component.scss'],
  standalone: false
})
export class UserGroupsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() initialClassification: OptionalClassification = 'All';
  @Input() existingGroups: GroupTreeNode[] = [];
  @Input() existingGroupIds: number[] = [];
  @Input() disabled: boolean;
  @Input() topLevelSelection: boolean;
  @Input() topLevelDisablesChildren: boolean;
  @Input() limitGroups: number[] | null = null;
  @Input() deselectedLimitedGroupsOnChange: boolean;
  @Input() showSelectedGroups: boolean = true;
  @Input() defaultTenantId: number | undefined;

  @Output() groupsChange: EventEmitter<GroupTreeNode[]> = new EventEmitter<GroupTreeNode[]>();

  topLevelGroups: { [key: string]: GroupNode[] } = {
    Public: [],
    Internal: [],
    All: [],
  };

  groups = new Map<number, GroupTreeNode>();
  parentToChildrenMap: Map<number, GroupNode[]> = new Map<number, GroupNode[]>();
  linkedChildToParentsMap: Map<number, GroupNode[]> = new Map<number, GroupNode[]>();

  selectedGroupNodes: GroupNode[] = [];

  classification: FormControl<OptionalClassification> = new FormControl('All');
  searchControl: FormControl<string> = new FormControl();
  tenantId: number;

  isLoading = false;
  hasLoadError = false;

  private subscriptions: Subscription = new Subscription();
  private previousRx$: Observable<unknown>;

  private maxParallelLoads = 2;
  private groupLoadQueue$ = new Subject<number>();

  get canAssignToParentGroups(): boolean {
    return this.authService.hasPermission('hickory.parent-groups-membership') || this.topLevelSelection;
  }

  get selectedGroups(): GroupTreeNode[] {
    return this.selectedGroupNodes.map((group) => group.group);
  }

  constructor(
    private groupsApi: GroupsApi,
    private authService: AuthService,
    @Inject(L10N_LOCALE) public locale: L10nLocale
  ) {}

  ngOnInit(): void {
    this.classification.setValue(this.initialClassification);
    this.tenantId = this.defaultTenantId ?? this.authService.user.snapshot?.tenantId;

    this.checkExistingGroups();
    this.subscribeToSearch();
    this.subscribeToClassification();
    this.subscribeToGroupLoadQueue();
    this.getParentGroups(this.classification.value, true);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.limitGroups) {
      this.tenantId = this.defaultTenantId ?? this.authService.user.snapshot.tenantId;

      this.onSearch(undefined, this.classification.value);
      if (this.deselectedLimitedGroupsOnChange) {
        const deselectedGroups = this.selectedGroupNodes.filter((group) => !changes.limitGroups?.currentValue?.includes(group.group.id));

        deselectedGroups?.forEach((group) => {
          this.onToggleGroup(group, false);
        });

        // remove any groups that are no longer in the limit groups
        this.groups = new Map<number, GroupTreeNode>(Array.from(this.groups).filter(([id, _group]) => changes.limitGroups?.currentValue?.includes(id)));
      }
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  checkExistingGroups(): void {
    for (const group of this.existingGroups) {
      this.selectedGroupNodes.push({ group: group, indentLevel: 0 });
    }

    if (!this.existingGroupIds?.length) {
      this.groupsChange.emit([]);
      return;
    }



    this.subscriptions.add(
      this.groupsApi
        .getGroupsV4(this.tenantId, this.existingGroupIds, undefined, 1, this.existingGroupIds.length)
        .pipe(
          tap((response) => {
            if (response.items.length > 0) {
              this.selectedGroupNodes = response.items.map((group) => {
                return { group, indentLevel: 0 };
              });

              this.populateGroups(response.items, null);
              this.recurseUpGroupTree(response.items);
              this.groupsChange.emit(this.selectedGroups);
            }
          })
        )
        .subscribe()
    );
  }

  getGroupById(groupId: number): Observable<GroupTreeNode> {
    return this.groupsApi.getGroupsV4(this.tenantId, [groupId], undefined, 1, 1).pipe(
      catchError((error) => {
        this.hasLoadError = true;
        return throwError(() => error);
      }),
      map((response) => (response.items ? response.items[0] : undefined))
    );
  }

  private currentlyLoadingGroups = new Set<number>();
  private getParentGroupTree(groupId: number) {
    this.currentlyLoadingGroups.add(groupId);
    this.groupLoadQueue$.next(groupId);
  }

  private loadGroupTree(groupId: number) {
    return this.getGroupById(groupId).pipe(
      switchMap((group) => {
        if (!group) {
          return of([undefined, undefined]);
        }
        if (group.parentId && !this.groups.has(group.parentId)) {
          return forkJoin([of(group), this.getGroupById(group.parentId)]);
        }

        return of([group, this.groups.get(group.parentId)]);
      }),
      tap(([group, parentGroup]: [GroupTreeNode | undefined, GroupTreeNode | undefined]) => {
        if (!group) {
          return;
        }
        this.hasLoadError = false;
        this.currentlyLoadingGroups.delete(groupId);
        this.populateGroups([group], parentGroup);
        this.recurseUpGroupTree([group]);
      }),
      finalize(() => this.currentlyLoadingGroups.delete(groupId))
    );
  }

  private recurseUpGroupTree(groups: GroupTreeNode[]) {
    const parentIds = new Set<number>();
    for (const group of groups) {
      if (group.parentId) {
        parentIds.add(group.parentId);
      }
    }

    for (const id of parentIds) {
      this.getParentGroupTree(id);
    }
  }

  subscribeToSearch(): void {
    this.subscriptions.add(
      this.searchControl.valueChanges
        .pipe(
          tap(() => this.searchRx?.unsubscribe()),
          debounceTime(300),
          tap((value: string) => {
            if (!value && !this.limitGroups?.length) {
              this.getParentGroups(this.classification.value, true);
            } else {
              this.onSearch(value, this.classification.value);
            }
          })
        )
        .subscribe()
    );
  }

  subscribeToGroupLoadQueue() {
    this.subscriptions.add(this.groupLoadQueue$.pipe(mergeMap((groupId: number) => this.loadGroupTree(groupId), this.maxParallelLoads)).subscribe());
  }

  subscribeToClassification(): void {
    this.subscriptions.add(
      this.classification.valueChanges
        .pipe(
          startWith(this.classification.value),
          pairwise(),
          tap(([prev, curr]) => {
            if (this.searchControl.value) {
              this.searchControl.setValue('', { emitEvent: false });
              this.topLevelGroups[prev] = [];
            }

            this.onClassificationChange(curr);
          })
        )
        .subscribe()
    );
  }

  onClassificationChange(classification: OptionalClassification): void {
    if (this.limitGroups?.length) {
      this.onSearch(undefined, classification);
    } else {
      this.getParentGroups(classification === 'All' ? null : classification);
    }
  }

  retryPreviousRequest(): void {
    if (this.previousRx$) {
      this.isLoading = true;
      this.subscriptions.add(this.previousRx$.subscribe());
    }
  }

  searchRx: Subscription;
  onSearch(value: string | undefined, classification: OptionalClassification | null = this.classification.value): void {
    // if limit groups is empty, don't make the call
    if (this.limitGroups && !this.limitGroups.length) {
      this.topLevelGroups[classification || 'All'] = [];
      return;
    }

    if (classification === 'All') {
      classification = null;
    }

    if (this.searchRx) {
      this.searchRx.unsubscribe();
    }

    this.isLoading = true;
    this.hasLoadError = false;
    this.previousRx$ = this.groupsApi
      .getGroupsV4(this.tenantId, this.limitGroups?.length ? this.limitGroups : undefined, undefined, 1, 500, value, classification as 'Public' | 'Internal' | null)
      .pipe(
        catchError((error) => {
          this.hasLoadError = true;
          return throwError(() => error);
        }),
        tap((groups: PagedGroups) => {
          this.hasLoadError = false;
          const type = classification || 'All';

          this.populateGroups(groups.items, null);

          this.topLevelGroups[type] = groups.items.map((group) => {
            return { group, indentLevel: 0 };
          });
        }),
        finalize(() => (this.isLoading = false))
      );

    this.searchRx = this.previousRx$.subscribe();
    this.subscriptions.add(this.searchRx);
  }

  populateGroups(groups: GroupTreeNode[], parentGroup: GroupTreeNode | null): void {
    if (parentGroup) {
      this.parentToChildrenMap.set(
        parentGroup.id,
        groups.map((group) => ({ group, indentLevel: 1 }))
      );
    }

    groups.forEach((group) => {
      if (this.groups.has(group.id)) {
        return;
      }

      this.groups.set(group.id, group);
    });

    groups.forEach((group) => {
      if (!parentGroup) {
        if (this.parentToChildrenMap.has(group.parentId)) {
          const children = this.parentToChildrenMap.get(group.parentId);
          if (!children?.some((c) => c.group.id === group.id)) {
            children.push({ group, indentLevel: 1 });
          }

          this.parentToChildrenMap.set(group.parentId, children);
        } else if (!this.parentToChildrenMap.has(group.parentId)) {
          this.parentToChildrenMap.set(group.parentId, [{ group, indentLevel: 1 }]);
        }
      }

      if (group.isLink && parentGroup) {
        if (this.linkedChildToParentsMap.has(group.id)) {
          const parents = this.linkedChildToParentsMap.get(group.id);
          if (!parents?.some((p) => p.group.id === parentGroup.id)) {
            parents.push({ group: parentGroup, indentLevel: 0 });
          }

          this.linkedChildToParentsMap.set(group.id, parents);
        } else {
          this.linkedChildToParentsMap.set(group.id, [{ group: parentGroup, indentLevel: 0 }]);
        }
      }
    });
  }

  parentGroupRx: Subscription;
  getParentGroups(classification: OptionalClassification | null = this.classification.value, refresh = false): void {
    if (this.limitGroups !== null) {
      this.topLevelGroups[classification || 'All'] = [];
      return;
    }

    if (!classification && this.topLevelGroups['All']?.length > 0 && !refresh) {
      return;
    }

    if (!!classification && this.topLevelGroups[classification]?.length > 0 && !refresh) {
      return;
    }

    if (classification === 'All') {
      classification = null;
    }

    if (this.parentGroupRx) {
      this.parentGroupRx.unsubscribe();
    }

    this.isLoading = true;
    this.hasLoadError = false;
    this.previousRx$ = this.groupsApi.getGroupTreeNodesV4(this.tenantId, null, classification as 'Public' | 'Internal' | null, 1, 500).pipe(
      catchError((error) => {
        this.hasLoadError = true;
        return throwError(() => error);
      }),
      tap((groups: PagedGroupTreeNodeResponse) => {
        this.hasLoadError = false;
        const type = classification || 'All';

        this.populateGroups(groups.items, null);

        this.topLevelGroups[type] = groups.items
          .filter((g) => !g.parentId || !this.groups.has(g.parentId))
          .map((group) => {
            return { group: group, indentLevel: 0 };
          });
      }),
      finalize(() => (this.isLoading = false))
    );

    this.parentGroupRx = this.previousRx$.subscribe();

    this.subscriptions.add(this.parentGroupRx);
  }

  onExpandNode(node: GroupNode): void {
    if (node.expanded) {
      node.expanded = false;
      return;
    }

    const existingChildren = this.getChildren(node);
    const allChildrenProperlyLoaded =
      this.hasChildren(node.group) && existingChildren?.length && existingChildren?.every((child) => child.group?.childGroupCount >= 0);

    if (this.hasChildren(node.group) && !allChildrenProperlyLoaded) {
      node.loading = true;
      this.previousRx$ = this.groupsApi.getGroupTreeNodesV4(this.tenantId, node.group.id, this.classification.value === 'All' ? null : this.classification.value, 1, 500).pipe(
        catchError((error) => {
          this.hasLoadError = true;
          return throwError(() => error);
        }),
        tap((children: PagedGroupTreeNodeResponse) => {
          this.hasLoadError = false;
          this.populateGroups(children.items, node.group);
          node.expanded = true;
        }),
        finalize(() => (node.loading = false))
      );

      this.subscriptions.add(this.previousRx$.subscribe());
    } else {
      node.expanded = true;
    }
  }

  onToggleGroup(node: GroupNode, checked: boolean | undefined): void {
    checked = checked ?? !this.isGroupSelected(node.group);

    if (checked) {
      this.onAddGroup(node);
    } else {
      this.onRemoveGroup(node);
    }

    this.groupsChange.emit(this.selectedGroups);
  }

  private onAddGroup(node: GroupNode): void {
    if (this.isGroupSelected(node.group)) {
      return;
    }

    this.selectedGroupNodes.push(JSON.parse(JSON.stringify(node)));
  }

  private onRemoveGroup(node: GroupNode): void {
    this.selectedGroupNodes = this.selectedGroupNodes.filter((group) => group.group.id !== node.group.id);

    this.removeDescendingChildren(node);
  }

  private removeDescendingChildren(node: GroupNode): void {
    const children = this.getChildren(node);
    children.forEach((child) => {
      this.selectedGroupNodes = this.selectedGroupNodes.filter((group) => group.group.id !== child.group.id);
      this.removeDescendingChildren(child);
    });
  }

  isNodeDisabled(node: GroupNode): boolean {
    let disabled = this.disabled;

    if (this.topLevelDisablesChildren) {
      disabled = disabled || this.isParentGroupSelected(node.group);
    }

    return disabled || (this.hasChildren(node.group) && !this.canAssignToParentGroups);
  }

  isGroupSelected(group: GroupTreeNode): boolean {
    if (!group) {
      return false;
    }

    return this.selectedGroupNodes.some((node) => node.group.id === group.id);
  }

  isParentGroupSelected(group: GroupTreeNode): boolean {
    if (!group) {
      return false;
    }

    const linked = this.linkedChildToParentsMap.get(group.id);
    if (!group?.parentId && !linked?.length) {
      return false;
    }

    const selectedParent = this.selectedGroupNodes.find((node) => node.group.id === group.parentId);
    if (selectedParent) {
      return true;
    }

    const parentGroup = this.groups.get(group.parentId);
    return this.isParentGroupSelected(parentGroup) || linked?.some((node) => this.isGroupSelected(node.group) || this.isParentGroupSelected(node.group));
  }

  isIndeterminate(node: GroupNode): boolean {
    if (this.isGroupSelected(node.group)) {
      return false;
    }

    const parentSelected = this.isParentGroupSelected(node.group);
    if (parentSelected) {
      return false;
    }

    const children = this.getChildren(node);
    if (this.hasChildren(node.group)) {
      return children.some((child) => this.isGroupSelected(child.group) || this.isIndeterminate(child));
    }

    return false;
  }

  getChildren(node: GroupNode): GroupNode[] {
    if (this.parentToChildrenMap.has(node.group.id)) {
      return this.parentToChildrenMap.get(node.group.id);
    }

    return [];
  }

  hasChildren(node: GroupTreeNode): boolean {
    return node.childGroupCount > 0 || this.parentToChildrenMap.has(node.id);
  }

  getIndentLevel(node: GroupNode): string {
    return node.indentLevel * 10 + 'px';
  }
}
